import { Radio, Stack } from '@chakra-ui/react'
import { Insurer } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioStack } from '@/containers/forms/RadioStack'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'

import {
  InputFormControl,
  RadioGroupFormControl,
  TextareaFormControl,
} from 'BrokerTsLib/src/components'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import type { Step4FormValues } from '../Step4'

export const KapitolMeetingRecordForm = () => {
  const { app } = useApp()
  const methods = useFormContext<Step4FormValues>()
  const { offer } = useSelectedQuote()
  const { isContracted, insuranceParams } = useInsuranceParams()

  // obcas pri prvnim renderu po sjednani zustaval jeste tento formular a padalo to. Neumim zatim resit jinak (callback na useEffect ve Step4.tsx nezabral)
  if (!methods) return null

  const { watch, setValue, clearErrors, control } = methods

  const isGcp = offer?.insurerId == Insurer.GeneraliCeskaPojistovna

  const showPlaceOfContract =
    isGcp ||
    offer?.insurerId == Insurer.CPP ||
    offer?.insurerId == Insurer.Uniqa

  const kapitolPozadavkyVSouladu = watch(
    'meetingRecord.complianceOfRequirements',
  )

  const reasonTextsKapitol = [
    'vybraný produkt je pro klienta cenově nejvýhodnější',
    'klient volí nejlepší poměr mezi požadovaným krytí rizik produktu a pojistnou ochranou',
    'klient preferoval nejnižší cenu produktu',
    'klient požadoval nejširší rozsah asistenčních služeb',
    'navržený limit pro povinné ručení byl doporučen s ohledem na rostoucí náklady za odškodnění, které mohou vzniknout v případě škody na zdraví a nákladům za poškození při dopravní nehodě na vozidlech a majetku třetích osob',
    'doplňková připojištění byla navržena dle specifických potřeb klienta, a to s ohledem na lokalitu míst, kde klient s vozidlem projíždí, parkuje. Zejména pro rizika, pojištění čelního skla, střetu se zvěří, asistenčních služeb',
    'riziko havarijního pojištění vozidla bylo navrženo s ohledem na klientovu potřebu, aby případnou výši škody nepokrýval ze svých finančních zdrojů',
    'doporučená výše spoluúčasti vychází z klientovi představy a finančním možnostem se na pojistné události podílet',
    'Pojištění GAP doporučuji s ohledem na datum pořízení vozidla, kdy hlavním smyslem tohoto připojištění je pokrýt rozdíl mezi pořizovací cenou vozidla a jeho aktuální tržní hodnotou v době totální škody nebo odcizení'
  ]

  const reasonTextsPropertyKapitol = [
    'vybraný produkt je pro klienta cenově nejvýhodnější',
    'jako jediný obsahuje tento produkt krytí rizik požadovaných klientem',
    'klient volí nejlepší poměr mezi požadovaným krytí rizik produktu a pojistnou ochranou',
    'klient preferoval nejnižší cenu produktu',
    'klient požadoval nejširší rozsah asistenčních služeb',
  ]

  const reasonTextsTravelKapitol = [
    'pro klienta jsou důležité vysoké limity pojistného plnění',
    'pojistné krytí pro případy sportovních aktivit',
    'klient poptával roční pojištění s opakovanými výjezdy',
    'jako jediný obsahuje tento produkt krytí rizik požadovaných klientem',
    'klient volí nejlepší poměr mezi požadovaným krytí rizik produktu a pojistnou ochranou',
    'klient preferoval nejnižší cenu produktu',
    'klient požadoval nejširší rozsah asistenčních služeb',
  ]  

  const showVehiclePrice = [Apps.vehicle, Apps.extra].includes(app as any) &&
    (insuranceParams as InsuranceParamsDto)?.vehicle?.price &&
    !isGcp

  const reasonTexts = app == Apps.travel ? reasonTextsTravelKapitol : 
    app == Apps.property ? reasonTextsPropertyKapitol :
      reasonTextsKapitol

  return (
    <FormBody>
      {!isGcp && (   
        <RadioGroupFormControl
          name="meetingRecord.complianceOfRequirements"
          label="Sjednávané pojištění a požadavky klienta"
          valueAsBoolean
          isDisabled={isContracted}
          control={control}
          onChange={(value) => {
            if (value) {
              setValue('meetingRecord.irregularities', '')
              clearErrors('meetingRecord.irregularities')
            }
          }}
        >        
          <Stack direction="row" spacing={16}>
            <Radio value="true">jsou v souladu</Radio>
            <Radio value="false">nejsou v souladu</Radio>
          </Stack>
        </RadioGroupFormControl>
      )}

      {kapitolPozadavkyVSouladu != null && !kapitolPozadavkyVSouladu! && (
        <TextareaFormControl
          label={
            <>
              Případné nesrovnalosti mezi požadavky Zákazníka a sjednávaným
              pojištěním nebo jeho podstatnou změnou
            </>
          }
          name="meetingRecord.irregularities"
          isDisabled={isContracted}
          control={control}
        />
      )}

      {!isGcp && (       
        <ReasonsFormControl
          isContracted={isContracted}
          options={reasonTexts}
        />
      )}

      {showVehiclePrice && (
        <RadioGroupFormControl
          name="meetingRecord.vehiclePriceDeterminedBy"
          label="Stanovení hodnoty pojištěného vozidla"
          isDisabled={isContracted}
          control={control}
        >
          <Stack direction="column" >
            <Radio value="klient">hodnotu vozidla sdělil klient</Radio>
            <Radio value="software">hodnota vozidla je stanovena softwarem</Radio>
            <Radio value="poradce">hodnotu vozidla stanovil poradce</Radio>
          </Stack>
        </RadioGroupFormControl>
      )}

      {showPlaceOfContract && (
        <FormRow>
          <InputFormControl
            name='meetingRecord.placeOfContract'
            label="Místo sjednání"
            isDisabled={isContracted}
            control={control}
          />
        </FormRow>
      )}
    </FormBody>
  )
}

const ReasonsFormControl = ({
  options,
  isContracted,
}: {
  options: string[]
  isContracted: boolean
}) => {
  const { control } = useFormContext<Step4FormValues>()
  return (
    <Stack>
      {!isContracted && (
        <RadioGroupFormControl
          name="meetingRecord.reasons"
          label="Důvody, na kterých je doporučení založeno"
          control={control}
        >
          <RadioStack variant="simple" sx={{ gridAutoFlow: 'row' }}>
            {options.map((option, index) => (
              <Radio key={index} value={option}>
                {option}
              </Radio>
            ))}
          </RadioStack>
        </RadioGroupFormControl>
      )}
      <TextareaFormControl
        label={
          isContracted ? 'Důvody, na kterých je doporučení založeno' : null
        }
        name="meetingRecord.reasons"
        isDisabled={isContracted}
        control={control}
      />
    </Stack>
  )
}

export default KapitolMeetingRecordForm
